import React, { useState, useEffect } from "react";
import Button from "../Button";
import Tray from "./Tray";
import { findActiveTrayForItem } from "./FetchTrays";
import { fetchJson, normalPost } from "../../lib/Fetching";
import TraySearch from "./TraySearch";
import TraysSection from "./TraysSection";
import TrayToolbar from "./TrayToolbar";
import LocationReport from "./LocationReport";
import MediaReport from "./MediaReport";
import styled from "styled-components";
import _ from "underscore";

const Section = styled.div`
  border-top: 1px solid #ddd;
  padding: 20px;
  background-color: #eee;
`;
export default function Trays({ itemId }) {
  const [activeTray, setActiveTray] = useState(null);
  const [available, setAvailable] = useState(null);
  const [awaitingScheduling, setAwaitingScheduling] = useState(null);
  const [scheduled, setScheduled] = useState(null);
  const [analysisStarted, setAnalysisStarted] = useState(null);
  const [reportedAndRetained, setReportedAndRetained] = useState(null);
  const [readyToDiscard, setReadyToDiscard] = useState(null);
  // const [discarded, setDiscarded] = useState(null);

  const [searchParams, setSearchParams] = useState({});
  const [showTechniqueNames, setShowTechniqueNames] = useState(false);
  const [selectedTrayIds, setSelectedTrayIds] = useState([]);

  const [displayOptions, setDisplayOptions] = useState({});
  const [showLocationReport, setShowLocationReport] = useState(false);
  const [showMediaReport, setShowMediaReport] = useState(false);

  const loadTrays = (state_id) =>
    fetchJson("/trays/trays.json", { state: state_id, ...searchParams });

  const loadAllTrays = () => {
    loadTrays([1, 2, 3, 4, 5, 6]).then((trays) => {
      const filter = (stateName) => trays.filter((t) => t.state === stateName);
      setAvailable(filter("available"));
      setAwaitingScheduling(filter("awaiting_scheduling"));
      setScheduled(filter("scheduled"));
      setAnalysisStarted(filter("analysis_started"));
      setReportedAndRetained(filter("reported_and_retained"));
      setReadyToDiscard(filter("ready_to_discard"));
      // setDiscarded(filter("discarded"));
    });
  };

  useEffect(
    () => {
      setSelectedTrayIds([]);
      loadAllTrays();
    },
    [searchParams]
  );

  useEffect(
    () => {
      if (!itemId) {
        if (activeTray) setActiveTray(null);
        return;
      }
      findActiveTrayForItem(itemId).then((json) => setActiveTray(json.tray));
    },
    [itemId]
  );

  const selectTray = (trayId) => {
    if (selectedTrayIds.filter((id) => id === trayId).length > 0) {
      setSelectedTrayIds(selectedTrayIds.filter((id) => id !== trayId));
    } else {
      setSelectedTrayIds(selectedTrayIds.concat(trayId));
    }
  };

  const selectTrays = (trayIds) => {
    trayIds.map((trayId) => selectedTrayIds.push(trayId));
    setSelectedTrayIds(_.uniq(selectedTrayIds));
  };

  const unSelectTrays = (trayIds) => {
    setSelectedTrayIds(selectedTrayIds.filter((id) => !trayIds.includes(id)));
  };

  const discardTrays = () => {
    if (!confirm("Are you sure you want to discard the selected trays?")) {
      return;
    }
    normalPost("/trays/trays/discard_trays", {
      tray_ids: selectedTrayIds,
    });
  };

  return (
    <div>
      <TraySearch onSearch={(searchParams) => setSearchParams(searchParams)} />
      {activeTray && (
        <div>
          <Button title="Close" icon="fa-times" small onClick={() => setActiveTray(null)} />
          <div className="basic-panel" style={{ marginBottom: 20, marginTop: -2 }}>
            <Tray
              key={activeTray.id}
              tray={activeTray}
              active
              style={{ marginBottom: 0 }}
              removeTray={() => {
                setActiveTray(null);
                loadAllTrays();
              }}
            />
          </div>
        </div>
      )}
      <div className="" style={{ marginTop: 10, border: 0 }}>
        <TrayToolbar
          displayOptions={displayOptions}
          setDisplayOptions={setDisplayOptions}
          defaultOptions={{
            showStateChangeDate: true,
            groupBy: "scheduled_date",
            priorityMatrixIds: [4, 3],
          }}
          toggleLocationReport={() => setShowLocationReport(!showLocationReport)}
          toggleMediaReport={() => setShowMediaReport(!showMediaReport)}
          selectedTrayCount={selectedTrayIds.length}
          discardTrays={discardTrays}
        />

        {showLocationReport &&
          selectedTrayIds.length > 0 && <LocationReport trayIds={selectedTrayIds} />}
        {showMediaReport &&
          selectedTrayIds.length > 0 && <MediaReport trayIds={selectedTrayIds} />}
        <TraysSection
          trays={available}
          title="Available / Empty"
          description="Empty Trays"
          activeTray={activeTray}
          showTechniqueNames={showTechniqueNames}
          setActiveTray={setActiveTray}
          selectedTrayIds={selectedTrayIds}
          selectTray={selectTray}
          displayOptions={displayOptions}
          selectTrays={selectTrays}
          unSelectTrays={unSelectTrays}
        />
        <TraysSection
          trays={awaitingScheduling}
          title="Awaiting Scheduling"
          description="Trays containing received samples that have not yet been scheduled"
          activeTray={activeTray}
          showTechniqueNames={showTechniqueNames}
          setActiveTray={setActiveTray}
          selectedTrayIds={selectedTrayIds}
          selectTray={selectTray}
          displayOptions={displayOptions}
          selectTrays={selectTrays}
          unSelectTrays={unSelectTrays}
        />
        <TraysSection
          trays={scheduled}
          title="Scheduled"
          description="Scheduled Trays that have not yet been scanned into an Item Session"
          activeTray={activeTray}
          showTechniqueNames={showTechniqueNames}
          setActiveTray={setActiveTray}
          selectedTrayIds={selectedTrayIds}
          selectTray={selectTray}
          displayOptions={displayOptions}
          selectTrays={selectTrays}
          unSelectTrays={unSelectTrays}
        />
        <TraysSection
          trays={analysisStarted}
          title="Analysis Started"
          description="Trays that have been scanned into a session"
          activeTray={activeTray}
          showTechniqueNames={showTechniqueNames}
          setActiveTray={setActiveTray}
          selectedTrayIds={selectedTrayIds}
          selectTray={selectTray}
          displayOptions={displayOptions}
          selectTrays={selectTrays}
          unSelectTrays={unSelectTrays}
        />
        <TraysSection
          trays={reportedAndRetained}
          title="Reported And Retained"
          description="All samples have been reported. After [x] days they will be ready to be discarded."
          activeTray={activeTray}
          showTechniqueNames={showTechniqueNames}
          setActiveTray={setActiveTray}
          selectedTrayIds={selectedTrayIds}
          selectTray={selectTray}
          displayOptions={displayOptions}
          selectTrays={selectTrays}
          unSelectTrays={unSelectTrays}
        />
        <TraysSection
          trays={readyToDiscard}
          title="Ready to Discard"
          description="These trays can be discarded."
          activeTray={activeTray}
          showTechniqueNames={showTechniqueNames}
          setActiveTray={setActiveTray}
          selectedTrayIds={selectedTrayIds}
          selectTray={selectTray}
          displayOptions={displayOptions}
          selectTrays={selectTrays}
          unSelectTrays={unSelectTrays}
        />
        {/* <TraysSection
          trays={discarded}
          title="Discarded"
          description="These trays have been discarded and can be re-used."
          activeTray={activeTray}
          showTechniqueNames={showTechniqueNames}
          setActiveTray={setActiveTray}
          selectedTrayIds={selectedTrayIds}
          selectTray={selectTray}
          displayOptions={displayOptions}
          selectTrays={selectTrays}
          unSelectTrays={unSelectTrays}
        /> */}
      </div>
    </div>
  );
}
